import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Users,
  Heart,
  TrendingUp,
  Search,
  UserPlus,
  Sparkles
} from 'lucide-react';
import Navigation from '../components/Navigation';

const JoinGroupPage = () => {
  const categories = [
    {
      icon: Users,
      title: 'Men',
      description: 'Connect with brothers in faith for accountability and growth',
      color: '#3B82F6'
    },
    {
      icon: Heart,
      title: 'Women',
      description: 'Join sisters in community, encouragement, and spiritual support',
      color: '#EC4899'
    },
    {
      icon: Sparkles,
      title: 'Young Adults',
      description: 'Build authentic friendships while navigating faith and life together',
      color: '#10B981'
    },
    {
      icon: TrendingUp,
      title: 'Leadership',
      description: 'Develop purpose-driven leadership skills with mentors and peers',
      color: '#CFA64B'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Search',
      description: 'Browse groups by category, interest, or location'
    },
    {
      number: '2',
      title: 'Connect',
      description: 'Reach out to group leaders and introduce yourself'
    },
    {
      number: '3',
      title: 'Grow',
      description: 'Join gatherings, build relationships, and deepen your faith'
    }
  ];

  const featuredGroups = [
    {
      image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&h=400&fit=crop',
      title: 'Downtown Young Professionals',
      category: 'Young Adults',
      members: '12 members',
      meetTime: 'Thursdays, 7 PM'
    },
    {
      image: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=600&h=400&fit=crop',
      title: 'Men of Purpose',
      category: 'Men',
      members: '8 members',
      meetTime: 'Saturdays, 9 AM'
    },
    {
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&h=400&fit=crop',
      title: 'Women Who Lead',
      category: 'Women',
      members: '15 members',
      meetTime: 'Wednesdays, 6:30 PM'
    },
    {
      image: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=600&h=400&fit=crop',
      title: 'Faith & Business Leaders',
      category: 'Leadership',
      members: '10 members',
      meetTime: 'Tuesdays, 7 PM'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/90 via-[#0B1B3B]/80 to-[#0B1B3B]/70" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Join a <span className="text-[#CFA64B]">Group</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Find community, grow in purpose, and walk together in faith.
          </p>
          <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
            Find a Group
          </Button>
        </div>
      </div>

      {/* Section 1: Group Categories */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Group Categories</h2>
            <p className="text-xl text-gray-600">Find the community that fits your season and calling</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {categories.map((category, idx) => {
              const Icon = category.icon;
              return (
                <Card 
                  key={idx}
                  className="border-2 border-gray-200 hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  style={{ borderColor: category.color }}
                >
                  <CardContent className="p-8 text-center">
                    <div 
                      className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center transition-colors"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <Icon className="w-10 h-10" style={{ color: category.color }} />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">{category.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{category.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 2: How It Works */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Three simple steps to find your community</p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute top-16 left-0 right-0 h-1 bg-[#CFA64B]/30" style={{ margin: '0 16.666%' }} />
            
            <div className="grid md:grid-cols-3 gap-8 relative">
              {steps.map((step, idx) => (
                <Card key={idx} className="border-2 border-gray-200 relative">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 bg-[#CFA64B] text-white rounded-full flex items-center justify-center text-3xl font-bold relative z-10">
                      {step.number}
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Featured Groups */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Featured Groups</h2>
            <p className="text-xl text-gray-600">Join one of these amazing communities</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredGroups.map((group, idx) => (
              <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={group.image} 
                    alt={group.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3 bg-[#CFA64B] text-[#0B1B3B] px-3 py-1 rounded-full text-xs font-bold">
                    {group.category}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">{group.title}</h3>
                  <div className="text-sm text-gray-600 space-y-1 mb-4">
                    <div>{group.members}</div>
                    <div>{group.meetTime}</div>
                  </div>
                  <Button variant="outline" className="w-full border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B] hover:text-white">
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Section 4: CTA Banner */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-10">
            Start your journey with people who believe in you.
          </h2>
          <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">
            Join Now
          </Button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Attendee Guide
            </a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              FAQs
            </a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default JoinGroupPage;
